<script lang="ts">
	import { useCtaServicesApiClient } from "$lib/api/CtaServicesApiClientFactory";
	import { Button } from "$lib/components/atoms/Button";
	import { CustomSelect, TextArea, TextField } from "$lib/components/atoms/Field";
	import { FormCard } from "$lib/components/organisms/Card/";
  import { UserPermissions } from ".";
  import { RequestInfoResponseModal } from "$lib/components/organisms/Modal";
  import { page } from '$app/stores';
  import { useOktaStore } from "$lib/utils/auth-store";
	import { useCampaignStore } from "$lib/utils/campaign-store";
	import { useSessionStore } from "$lib/utils/session-store";
  import { useHistoryStore } from "$lib/utils/history-store";
  import { GtmStore } from "$lib/utils/gtm-store";
  import { useCTAFlow } from '$lib/utils/cta-flow';
  import { field, form } from "svelte-forms";
	import { get } from "svelte/store";
  import {
    required as requiredValidation,
    email as emailValidation,
    pattern as patternValidation,
  } from 'svelte-forms/validators';
  import type { InformationRequestRequest } from "$lib/api/generated/cta-services";
  import type { SessionModel } from '@24hbs/utils';
  import { openModal } from "$lib/utils/modals";
  import { goto } from '$app/navigation';
	import { createCtaEvent } from "$lib/utils/cta-flow/helpers";
	import { phoneFormatter, sha256Hex } from "$lib/utils/formatter";
  import type { IInitiative, INewsFields } from '$lib/utils/contentful/types';

  export let requestInfoType: 'generic' | 'b2b' | 'course' | 'sedi' | 'initiative' | 'news';
  export let selectedSession: SessionModel | null = null;
  export let targetB2B: 'le-imprese' | 'job-finding' | 'skilling' | 'branding' | 'finance' | null = null;
  export let initiative: IInitiative | null = null;
  export let news: INewsFields | null = null;
  export let customSubject: string | null = null;

  const sessionStore = useSessionStore();
  const authStore = useOktaStore();
  const isLoggedIn = authStore.isLoggedIn;
  const userInfo = authStore.userInfo;
  const pendingUserInfo = sessionStore.pendingUserInfo;
  const companyStatus = sessionStore.companyStatus;
  const ctaService = useCtaServicesApiClient();

  const topic = field(
    'topic',
    '',
    requestInfoType === 'generic' ? [requiredValidation()] : [],
    { valid: false }
  );
  const name = field(
    'name',
    $userInfo?.name || $pendingUserInfo?.name || '',
    [requiredValidation()],
    { valid: false }
  );
  const surname = field(
    'surname',
    $userInfo?.surname || $pendingUserInfo?.lastname || '',
    [requiredValidation()],
    { valid: false }
  );
  const email = field(
    'email',
    $userInfo?.email || $pendingUserInfo?.email || '',
    [emailValidation()],
    { valid: false }
  );
  const phone = field(
    'phone',
    $userInfo?.phone || $pendingUserInfo?.phone || '',
    [requiredValidation(), patternValidation(/^[0-9]+$/)],
    { valid: false }
  );
  const role = field(
    'role',
    '',
    [],
    { valid: false }
  );
  const company = field(
    'company',
    '',
    [],
    { valid: false }
  );
  const piva = field(
    'piva',
    '',
    [],
    { valid: false }
  );
  const message = field(
    'message',
    '',
    [requiredValidation()],
    { valid: false }
  );
  const subscribe = field(
    'subscribe',
    $userInfo?.subscribe || $pendingUserInfo?.subscribe || false,
    [],
    { valid: false }
  );
  const businessUseConsent = field(
    'businessUseConsent',
    $userInfo?.businessUseConsent || $pendingUserInfo?.businessUseConsent || false,
    [],
    { valid: false }
  );
  const profilingConsent = field(
    'profilingConsent',
    $userInfo?.profilingConsent || $pendingUserInfo?.profilingConsent || false,
    [],
    { valid: false }
  );

  const requestInfo = form(topic, name, surname, email, phone, role, company, piva, message, subscribe, businessUseConsent, profilingConsent);

  let isSubmitting: boolean = false;
  let ctaLoading: boolean = false;
  let genericError: null | string = null;
  let honey: string = '';

  const handleAuthStoreUpdate = async () => {
    switch (requestInfoType) {
      case 'course':
      case 'initiative':
        // If user is logged in update with the values that he set
        if ($isLoggedIn) {
            await authStore.updateUserProfile({
              ...$userInfo,
              name: $name.value,
              surname: $surname.value,
              phone: $phone.value,
            });
          }
        break;
    }
  }

  const handleHistoryStoreUpdate = () => {
    switch (requestInfoType) {
      case 'course':
        const historyStore = useHistoryStore();
        historyStore.addHistoryEntry('request_info', selectedSession);
        break;
    }
  }
  
  const pushInformationRequestGtm = () => {
    switch (requestInfoType) {
      case 'generic':
        GtmStore.pushEvent({
            event: 'BBevent',
            category: 'Richiedi Informazioni',
            action: 'Invio-OK',
            label: honey,
            event_id: '4',
          },
          false
        );
        break;
      case 'b2b':
          GtmStore.pushEvent({
            event: 'BBevent',
            category: "RichiestaInfo_B2b",
            action: "Conferma",
            event_id: '35',
            user_bsid: $isLoggedIn && $userInfo?.bsid ? $userInfo.bsid : $pendingUserInfo ? $pendingUserInfo.bsid : 'NOT SET',
            user_hashed_email: $isLoggedIn && $userInfo?.email ? sha256Hex($userInfo.email) : $pendingUserInfo ? sha256Hex($pendingUserInfo.email) : 'NOT SET'
          },
          false
        );
        break;
      case 'sedi':
        GtmStore.pushEvent({
            pageCategory: 'typ_info_sedi',
          });
        GtmStore.pushPageView();
        break;
      case 'initiative':
        GtmStore.pushEvent({
            event: 'BBevent',
            category: 'Richiedi Informazioni',
            action: 'Invio-OK',
            label: initiative?.fields?.title ? initiative.fields.title : '',
            event_id: '4',
          },
          false
        );
        break;
      case 'news':
        GtmStore.pushEvent({
            event: 'BBevent',
            category: 'Richiedi Informazioni',
            action: 'Invio-OK',
            label: news?.fields?.title ? news.fields.title : '',
            event_id: '4',
          },
          false
        );
        break;
    }
  }

  const getInformationSubject = () => {
    switch (requestInfoType) {
      case 'generic':
        return $topic.value;
      case 'b2b':
        let subject = '';
        if (customSubject) {
          subject = customSubject;
        } else {
          switch (targetB2B) {
            case 'le-imprese':
              subject = 'B2B_PER_LE_IMPRESE';
              break;
            case 'job-finding':
              subject = 'B2B_JOB_FINDING';
              break;
            case 'skilling':
              subject = 'B2B_SKILLING';
              break;
            case 'branding':
              subject = 'B2B_LE_IMPRESE';
              break;
            case 'finance':
              subject = 'B2B_FINANCE';
              break;
          }
        }
        return `${subject} ${$company ? $company.value : ''} ${$role ? $role.value : ''}`;
      case 'course':
        return undefined;
      case 'sedi':
        return '';
      case 'initiative':
        return 'event-' + initiative?.fields.title
      case 'news':
        return 'news-' + news?.fields.title
    }
  };

  const getCourseUrl = () => {
    if (selectedSession === null) {
      return undefined
    }
    if (requestInfoType === 'b2b') {
      return `https://${$page.url.host}/b2b/${$page.params.type.split("b2b-")[1]}/${selectedSession.slug}`;
    }
    if (requestInfoType === 'course') {
      return `https://${$page.url.host}/formazione/${selectedSession.slug}/${selectedSession.idSession}`;
    }
  };

  const handleRequestInfoRespose = () => {
    if (requestInfoType === 'generic' || requestInfoType === 'sedi' || requestInfoType === 'news') {
      openModal(RequestInfoResponseModal, { isSuccess: true });
    }
    if (requestInfoType === 'initiative') {
      openModal(RequestInfoResponseModal, { isSuccess: true, callBackPath: `/eventi/${initiative?.fields.slug}` });
    }
    if (requestInfoType === 'b2b') {
      goto('/b2b/thankyou', { replaceState: true });
    }
    if (requestInfoType === 'course') {
      const ctaFlow = useCTAFlow();
      const confirmationPath = `/formazione/${selectedSession?.slug}/${selectedSession?.idSession}/request_information_result`;
      if (ctaFlow.shouldGoToCompletionForm(true)) {
        goto(`/registrati/completion?callback=${confirmationPath}`, { replaceState: true });
      } else {
        goto(confirmationPath, { replaceState: true });
      }
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    await requestInfo.validate();

    if ($requestInfo.valid) {
      isSubmitting = true;

      const campaignStore = useCampaignStore();
      const campaignData = get(campaignStore.campaignData);
      const firstClickCampaign = get(campaignStore.firstClickCampaignData);
      const webid = get(campaignStore.webid);

      const informationReq: InformationRequestRequest = {
        type: requestInfoType === 'b2b' ? 'b2bInfoRequest' : undefined,
        bsid: $isLoggedIn && $userInfo?.bsid ? $userInfo?.bsid : $pendingUserInfo ? $pendingUserInfo.bsid : '',
        id: $isLoggedIn && $userInfo && $userInfo.id ? $userInfo.id : $pendingUserInfo ? $pendingUserInfo.id : '',
        name: $name.value,
        surname: $surname.value,
        email: $email.value,
        phone: $phone.value,
        piva: requestInfoType === 'b2b' ? $piva.value : undefined,
        message: $message.value,
        consent1: $subscribe.value,
        consent2: $businessUseConsent.value,
        consent3: $profilingConsent.value,
        informationSubject: getInformationSubject(),
        b2b: requestInfoType === 'b2b' ? true : $companyStatus,
        productCode: selectedSession !== null ? selectedSession.productCode : undefined,
        courseTitle: selectedSession !== null ? selectedSession.title : undefined,
        courseUrl: getCourseUrl(),
        category: selectedSession !== null ? selectedSession.category : undefined,
        courseTypeFull: selectedSession !== null ? selectedSession.courseTypeFull : undefined,
        price: selectedSession !== null ? selectedSession.priceNoVat : undefined,
        discountPrice: selectedSession !== null ? selectedSession.discountPrice : undefined,
        brochureAvailable: selectedSession !== null ? selectedSession.brochureAvailable : undefined,
        brochureBooking: selectedSession !== null ? selectedSession.brochureBooking : undefined,
        brochureUrl: selectedSession !== null ? selectedSession.brochureUrl : undefined,
        courseReferrerName: selectedSession !== null && selectedSession.sessionContacts !== null && selectedSession.sessionContacts.editorialContact ? selectedSession.sessionContacts.editorialContact : undefined,
        courseReferrerEmail: selectedSession !== null && selectedSession.sessionContacts !== null && selectedSession.sessionContacts.editorialEmail ? selectedSession.sessionContacts.editorialEmail : undefined,
        referrer: campaignData?.referrer,
        utmCampaignSource: campaignData?.utm_source,
        utmCampaignMedium: campaignData?.utm_medium,
        utmCampaignName: campaignData?.utm_campaign,
        utmCampaignTerm: '',
        utmCampaignContent: '',
        fbclid: campaignData?.fbclid,
        gclid: campaignData?.gclid,
        gclsrc: campaignData?.gclsrc,
        websiteSessionData: {
          referrer: firstClickCampaign?.referrer,
          utmCampaignSource: firstClickCampaign?.utm_source,
          utmCampaignMedium: firstClickCampaign?.utm_medium,
          utmCampaignName: firstClickCampaign?.utm_campaign,
          utmCampaignTerm: '',
          utmCampaignContent: '',
          fbclid: firstClickCampaign?.fbclid,
          gclid: firstClickCampaign?.gclid,
          gclsrc: firstClickCampaign?.gclsrc,
          type: firstClickCampaign?.type,
          webid: webid,
        },
        device: firstClickCampaign?.device,
      };

        try { 
          await ctaService.submitInformationRequest(informationReq, createCtaEvent('contact_request'))
          requestInfo.reset();
          handleAuthStoreUpdate();
          handleHistoryStoreUpdate();
          pushInformationRequestGtm();
          handleRequestInfoRespose();
        } catch (error) {
          openModal(RequestInfoResponseModal);
          console.error('error informationRequest', error);
          genericError = "Si è verificato un problema durante l'invio. Prova a modificare i dati inseriti oppure riprova in un secondo momento.";
        } finally {
          isSubmitting = false;
        }
    } else {
      isSubmitting = false;
    }
  }
</script>

<form class="request-info-form" on:submit|preventDefault={handleSubmit}>
  <FormCard requiredFields>
    {#if requestInfoType === 'initiative'}
      <div class="col-span-full">
        <h6>Richiesta informazioni per <strong>{initiative?.fields.title}</strong></h6>
      </div>
    {/if}
    {#if requestInfoType === 'news'}
      <div class="col-span-full">
        <h6>Richiesta informazioni per <strong>{news?.fields.title}</strong></h6>
      </div>
    {/if}
    {#if requestInfoType === 'generic'}
      <CustomSelect
        class="col-span-full"
        name="topic"
        id="topic"
        label="Argomento della richiesta*"
        bind:value={$topic.value}
        options={[
          { value: 'generic', label: 'Informazioni generali' },
          { value: 'become-partner', label: 'Diventa partner' },
          { value: 'event-information', label: 'Informazioni su iniziative' },
        ]}
        error={
          $requestInfo.hasError('topic.required')
            ? 'Inserisci l\'argomento della richiesta'
            : null
        }
      />
      <input autocomplete="off" tabIndex="-1" name="url" style="display: none" bind:value={honey} />
    {/if}
    <TextField
      class="col-span-full md:col-span-3"
      name="name"
      id="name"
      type="text"
      label="Nome"
      disabled={isSubmitting || ctaLoading}
      autocomplete="given-name"
      bind:value={$name.value}
      required
      error={
        $requestInfo.hasError('name.required')
          ? 'Inserisci il tuo nome'
          : null
      }
    />
    <TextField
      class="col-span-full md:col-span-3"
      name="last-name"
      id="last-name"
      type="text"
      label="Cognome"
      disabled={isSubmitting || ctaLoading}
      autocomplete="family-name"
      bind:value={$surname.value}
      required
      error={
        $requestInfo.hasError('surname.required')
          ? 'Inserisci il tuo cognome'
          : null
      }
    />
    <TextField
      class="col-span-full md:col-span-3"
      name="email"
      id="email"
      type="email"
      label="Email"
      disabled={isSubmitting || ctaLoading}
      bind:value={$email.value}
      autocomplete="email"
      required
      error={
        $requestInfo.hasError('email.not_an_email')
          ? 'L’indirizzo e-mail non risulta valido, controlla l’indirizzo inserito'
          : null
      }
    />
    <TextField
      class="col-span-full md:col-span-3"
      name="phone"
      id="phone"
      type="text"
      valueFormatter={phoneFormatter}
      label="Telefono"
      disabled={isSubmitting || ctaLoading}
      bind:value={$phone.value}
      autocomplete="tel"
      required
      error={
        $requestInfo.hasError('phone.required')
          ? 'Inserisci il tuo numero di telefono'
          : null
      }
    />
    {#if requestInfoType === 'b2b'}
      <TextField
        class="col-span-full md:col-span-3"
        name="role"
        id="role"
        type="text"
        label="Ruolo"
        disabled={isSubmitting || ctaLoading}
        autocomplete="role"
        bind:value={$role.value}
      />
      <TextField
        class="col-span-full md:col-span-3"
        name="company"
        id="company"
        type="text"
        label="Azienda"
        disabled={isSubmitting || ctaLoading}
        autocomplete="company"
        bind:value={$company.value}
      />
      <TextField
        class="col-span-full md:col-span-3"
        name="piva"
        id="piva"
        type="text"
        label="P.IVA"
        disabled={isSubmitting || ctaLoading}
        autocomplete="piva"
        bind:value={$piva.value}
      />
    {/if}
    <TextArea
      class="col-span-full"
      name="message"
      id="message"
      label="Messaggio"
      disabled={isSubmitting || ctaLoading}
      bind:value={$message.value}
      pla
      required
      error={
        $requestInfo.hasError('message.required')
          ? 'Inserisci un messaggio'
          : null
      }
    />
    {#if !$isLoggedIn}
      <UserPermissions
        bind:subscribe={$subscribe.value}
        bind:businessUseConsent={$businessUseConsent.value}
        bind:profilingConsent={$profilingConsent.value}
        disabled={isSubmitting || ctaLoading}
        showPrivacy
      />
    {/if}
    {#if genericError}
      <p class="generic-error col-span-full">
        {genericError}
      </p>
    {/if}
  </FormCard>

  <Button
    class="col-span-full mt-8 text-center"
    type="submit"
    isLoading={isSubmitting || ctaLoading}
    disabled={isSubmitting || ctaLoading}
  >
    Invia Richiesta
  </Button>
</form>